import React, { Fragment, useState, useRef, useReducer } from 'react'
import PropTypes from 'prop-types'
import { mapValues } from 'lodash'
import { Container, Heading } from '@scentregroup/ui'
import classNames from 'classnames'
import { useQueryParams, ArrayParam, StringParam } from 'use-query-params'
import WhatsOnList from '../../../components/whats-on-list'
import withModules from '../../../containers/with-modules'
import withWhatsOnList from '../../../containers/with-whats-on-list'
import pagesHoc from '../../pages-hoc'
import getEntryStructuredData, {
  getStructureDataGqlV2,
} from '../../../helpers/get-entry-structured-data'
import { WhatsOnSeeMoreContext } from '../../../context'
import { capitalize, isEmpty } from '../../../helpers'
import { absoluteUrl } from '../../../helpers/url'
import Modules from '../../../components/modules'
import { getNowInCentreInDateFormat } from '../../../helpers/date-helpers'
import {
  rangesFromOccurrencesListsAndFilters,
  getOcccurrencesLists,
} from '../../../components/whats-on-list/contentful-to-ranges'
import WhatsHappeningFilters from '../../../components/whats-happening-filters'
import withWhatsOnFilters from '../../../containers/with-whats-on-filters'
import canonicalLink from '../../../helpers/canonical-link'
import EventNotification from '../../../components/event-notification'
import { filterRanges, calucateCount, makeMultiFilterProps } from './helper'
import styles from './index.module.scss'

const QUERY_NAME = {
  DATE_RANGE: 'range',
  CONTENT_TYPES: 'types',
  TOPICS: 'topics',
  EXPERIENCES: 'experiences',
}

const CONTENT_TYPES_FILTERS = ['Events', 'Offers']

function reducer(state, action) {
  switch (action.type) {
    case 'expand_see_more':
      return { ...state, [action.range]: true }
    case 'collapse_see_more':
      if (action.range) {
        return { ...state, [action.range]: false }
      }
      return mapValues(state, _item => false)
    default:
      return { ...state }
  }
}

export function WhatsHappening(props) {
  const {
    events,
    offers,
    centre,
    template,
    modules,
    topicFilters,
    experienceFilters,
  } = props

  const [state, dispatch] = useReducer(reducer, {})
  const [query, setQuery] = useQueryParams({
    range: StringParam,
    types: ArrayParam,
    topics: ArrayParam,
    experiences: ArrayParam,
  })
  const { range: dateRange, types: contentTypes, topics, experiences } = query
  const makeSetter = paramName => val =>
    setQuery({ [paramName]: val }, 'pushIn')
  const [setDateRange, setContentTypes, setTopics, setExperiences] = [
    QUERY_NAME.DATE_RANGE,
    QUERY_NAME.CONTENT_TYPES,
    QUERY_NAME.TOPICS,
    QUERY_NAME.EXPERIENCES,
  ].map(makeSetter)
  const onResetFilters = () => {
    dispatch({ type: 'collapse_see_more' })
    return setQuery({}, 'push')
  }
  const { current: baseDate } = useRef(
    getNowInCentreInDateFormat(centre.timeZone)
  )

  const [occurrencesLists] = useState(() =>
    getOcccurrencesLists(baseDate, events, offers, centre)
  )

  // Props for the filters
  const contentTypesProps = makeMultiFilterProps(
    contentTypes,
    setContentTypes,
    CONTENT_TYPES_FILTERS
  )
  const topicsProps = makeMultiFilterProps(topics, setTopics, topicFilters)
  const experiencesProps = makeMultiFilterProps(
    experiences,
    setExperiences,
    experienceFilters
  )
  const dateRangeProps = {
    baseDate,
    selectedDateRange: dateRange,
    onChange: setDateRange,
    dataTestId: 'date-range-filters',
  }

  // Values of selected filters to do the actual filtering
  const whatsOnContentId = 'whats-on-content'
  const whatsOnList = filterRanges(
    rangesFromOccurrencesListsAndFilters(baseDate, occurrencesLists, {
      contentTypes,
      experiences,
      topics,
      dateRange,
    }),
    dateRange
  )
  const count = calucateCount(whatsOnList)
  return (
    <Container type="large" className={styles.whatsHappeningContainer}>
      <Heading
        type="large"
        className={classNames(
          styles.whatsHappeningHeading,
          styles.whatsHappeningPageHeading
        )}
      >
        {"What's happening"}
      </Heading>
      <Modules modules={modules || []} />
      <EventNotification className={styles.warningNotification} />
      <Fragment>
        <div className={styles.whatsOn}>
          <div
            aria-controls={whatsOnContentId}
            className={styles.whatsOnFilters}
          >
            <WhatsHappeningFilters
              {...{
                dateRangeProps,
                onResetFilters,
                contentTypesProps,
                topicsProps,
                experiencesProps,
                count,
              }}
            />
          </div>
          <WhatsOnSeeMoreContext.Provider value={{ state, dispatch }}>
            <WhatsOnList
              ranges={whatsOnList}
              analyticsDisplayId={template?.analyticsMetadata?.displayId}
            />
          </WhatsOnSeeMoreContext.Provider>
        </div>
      </Fragment>
    </Container>
  )
}

WhatsHappening.propTypes = {
  topicFilters: PropTypes.array,
  experienceFilters: PropTypes.array,
  events: PropTypes.array,
  offers: PropTypes.array,
  centre: PropTypes.object,
  template: PropTypes.shape({
    analyticsMetadata: PropTypes.shape({
      displayId: PropTypes.string,
    }),
  }),
  modules: PropTypes.array,
}

WhatsHappening.defaultPropTypes = {
  modules: [],
}

const getMetaData = ({ events, centre, canonical }) => {
  let schema = {}
  try {
    schema = getStructureDataGqlV2(events, 'Event', centre)
      .map(item =>
        getEntryStructuredData({
          entry: {
            ...item,
            category: capitalize(item.category),
            title: item.name,
          },
          centre,
          url: absoluteUrl(
            `${item.category}/${item.sys.id}/${item.slug}`,
            canonical
          ),
        })
      )
      .filter(structuredData => !isEmpty(structuredData))
  } catch (e) {
    // Swallow the error; we don't want to 404 on a structured data problem
    // TODO maybe consider logging to Sentry
  }

  return {
    title: `Westfield ${centre.title} Events | What's happening in ${centre.title}`,
    description: `Westfield ${centre.title} - Explore our fun events, activities, offers & more. Check out what's happening at Westfield ${centre.title}!`,
    schema,
  }
}

export default withWhatsOnFilters(
  withModules(
    withWhatsOnList(
      pagesHoc(WhatsHappening, getMetaData, canonicalLink.whatsHappening)
    )
  )
)
